import { createSelector } from '@ngrx/store';
import { Seq } from 'immutable';
import {
  CardStatus,
  OperationStatus,
  RequestStatus,
  UnitStatus,
  VehicleType,
} from 'src/graphql/generated/graphql.generated';
import { selectTransportOffersByRequestId } from 'src/store/entityStores/transport-offer-selectors';
import {
  CardSummaryRecord,
  selectAllCardSummaryEntities,
} from 'src/store/generated/card-summary-record.generated';
import {
  selectAllUnitSummaryEntities,
  selectUnitSummaryEntitiesByCompanyId,
  selectUnitSummaryEntitiesByRegionId,
  UnitSummaryRecord,
} from 'src/store/generated/unit-summary-record.generated';
import {
  selectAllVehicleSummaryEntities,
  VehicleSummaryRecord,
} from 'src/store/generated/vehicle-summary-record.generated';
import { getOrElse } from 'src/util/values';
import {
  selectVehicleControlZoneEntitiesByCompanyId,
  selectVehicleControlZoneEntitiesByControlZoneId,
} from '../generated/vehicle-control-zone-record.generated';
import {
  selectBaseControlZoneEntitiesByCompanyId,
  selectBaseControlZoneEntitiesByControlZoneId,
} from '../generated/base-control-zone-record.generated';
import { selectAllUnitPostions, selectAllUnits } from './entity-store-selectors';
import { selectOperationByCardId, selectOperationByCardNumber } from './shared-selectors';

export function selectUnitsByCompany(companyId: string) {
  return createSelector(selectAllUnits, (units) =>
    units.valueSeq().filter((u) => u.companyId === companyId),
  );
}

export function selectUnitSummariesByControlZoneCompanyId(
  companyId: string,
  includeDisabled: boolean = false,
) {
  return createSelector(
    selectAllUnitSummaryEntities,
    selectVehicleControlZoneEntitiesByCompanyId(companyId),
    selectBaseControlZoneEntitiesByCompanyId(companyId),
    (units, vehiclesControlZones, baseControlZones) => {
      const vids = vehiclesControlZones.map((v) => v.vehicleId);
      const baseIds = baseControlZones.map((b) => b.baseId);
      return units.valueSeq().filter((u) => {
        if (u.vehicleId == null || (!vids.contains(u.vehicleId) && !baseIds.contains(u.baseId))) {
          return false;
        }

        return includeDisabled || u.isEnabled;
      });
    },
  );
}
export function selectUnitSummariesByControlZoneId(
  controlZoneId: string,
  includeDisabled: boolean = false,
) {
  return createSelector(
    selectAllUnitSummaryEntities,
    selectVehicleControlZoneEntitiesByControlZoneId(controlZoneId),
    selectBaseControlZoneEntitiesByControlZoneId(controlZoneId),
    (units, vehiclesControlZones, baseControlZones) => {
      const vids = vehiclesControlZones.map((v) => v.vehicleId);
      const baseIds = baseControlZones.map((b) => b.baseId);
      return units.valueSeq().filter((u) => {
        if (u.vehicleId == null || (!vids.contains(u.vehicleId) && !baseIds.contains(u.baseId))) {
          return false;
        }
        return includeDisabled || u.isEnabled;
      });
    },
  );
}
export function selectUnitSummariesByCompanyId(
  companyId: string,
  includeDisabled: boolean = false,
) {
  return createSelector(selectUnitSummaryEntitiesByCompanyId(companyId), (units) =>
    units.valueSeq().filter((u) => {
      if (includeDisabled) {
        return true;
      }

      return u.isEnabled;
    }),
  );
}

export function selectUnitSummariesByRegionId(
  regionId: string,
  includeHidden: boolean = true,
  includeDisabled: boolean = false,
) {
  return createSelector(selectUnitSummaryEntitiesByRegionId(regionId), (units) => {
    return units
      .filter((u) => byHiddenStatus(includeHidden))
      .filter((u) => {
        if (includeDisabled) {
          return true;
        }

        return u.isEnabled;
      });
  });
}

export function selectUnitSummariesByRegions(
  regionIds: string[],
  includeHidden: boolean = true,
  nameOrTailLike?: string,
  includeDisabled: boolean = false,
  ...status: UnitStatus[]
) {
  return createSelector(
    selectAllUnitSummaryEntities,
    selectAllVehicleSummaryEntities,
    (units, vehicles) => {
      return units
        .valueSeq()
        .filter((u) => {
          if (includeDisabled) {
            return true;
          }

          return u.isEnabled;
        })
        .filter((u) => regionIds.includes(u.regionId))
        .filter(byHiddenStatus(includeHidden))
        .filter((c) => status.length === 0 || status.includes(c.status))
        .filter((unit) => matchUnitOrVehicle(vehicles.toIndexedSeq(), unit, nameOrTailLike));
    },
  );
}

export function selectUnitSummariesByRegionsAndUnits(
  regionIds: string[],
  unitIds: string[],
  includeHidden: boolean = true,
  nameOrTailLike?: string,
  includeDisabled: boolean = false,
  ...status: UnitStatus[]
) {
  return createSelector(
    selectAllVehicleSummaryEntities,
    selectUnitSummariesByRegions(regionIds, includeHidden, undefined, includeDisabled, ...status),
    (vehicles, units) => {
      return units
        .valueSeq()
        .filter(
          (u) =>
            unitIds.includes(u.id) ||
            matchUnitOrVehicle(vehicles.toIndexedSeq(), u, nameOrTailLike),
        );
    },
  );
}

export function selectUnitSummariesByCurrentCompany(
  currentCompanyId: string,
  excludeDispatchByCurrentCompany: boolean,
  nameOrTailLike?: string,
  companyIds: string[] = [],
  vehicleTypes: VehicleType[] = [],
) {
  const canIncludeCompany = (companyId: string) =>
    companyIds.length === 0 || companyIds.includes(companyId);
  const canIncludeVehicle = (vehicle: VehicleSummaryRecord) =>
    vehicle.companyId === currentCompanyId &&
    (!excludeDispatchByCurrentCompany ||
      (vehicle.dispatchCompanyId != null && vehicle.dispatchCompanyId !== currentCompanyId)) &&
    (vehicleTypes.length === 0 || vehicleTypes.includes(vehicle.type));
  return createSelector(
    selectAllVehicleSummaryEntities,
    selectAllUnitSummaryEntities,
    (vehicles, units) => {
      // first filter most of the vehicles and units before calling matchUnitOrVehicle()
      vehicles = vehicles.filter((v) => canIncludeVehicle(v));
      const vehicleIds = vehicles.map((v) => v.id).toIndexedSeq();
      const vehiclesSeq = vehicles.toIndexedSeq();
      units = units.filter(
        (u) =>
          u.vehicleId != null && canIncludeCompany(u.companyId) && vehicleIds.includes(u.vehicleId),
      );
      return units
        .filter((unit) => matchUnitOrVehicle(vehiclesSeq, unit, nameOrTailLike))
        .valueSeq();
    },
  );
}

export function selectUnitByCardNumber(cardNumber: string) {
  return createSelector(
    selectOperationByCardNumber(cardNumber),
    selectAllUnits,
    (operation, units) => {
      return operation == null ? undefined : units.find((unit) => unit.id === operation.unitId);
    },
  );
}

export function selectUnitByCardId(cardId: string) {
  return createSelector(selectOperationByCardId(cardId), selectAllUnits, (operation, units) => {
    return operation == null ? undefined : units.find((unit) => unit.id === operation.unitId);
  });
}

export function selectUnitSummaryByCardId(cardId: string) {
  return createSelector(
    selectOperationByCardId(cardId),
    selectAllUnitSummaryEntities,
    (operation, units) => {
      return operation == null ? undefined : units.find((unit) => unit.id === operation.unitId);
    },
  );
}

export function selectUnitSummaryByVehicleId(vehicleId: string) {
  return createSelector(selectAllUnitSummaryEntities, (units) => {
    return units.find((u) => u.vehicleId === vehicleId);
  });
}

export function selectNonThirdPartyUnitByBaseId(baseId: string) {
  return createSelector(selectAllUnits, (units) => {
    return units.find(
      (u) => u.baseId === baseId && u.isHidden !== true && u.isThirdPartyUnit !== true,
    );
  });
}

export function selectAvailableUnitsByCompanyId(companyId: string) {
  return createSelector(
    selectUnitSummaryEntitiesByCompanyId(companyId),
    selectAllCardSummaryEntities,
    (units, operations) => {
      return units
        .valueSeq()
        .filter(byStatusAndNonActiveOperations(UnitStatus.Available, operations.valueSeq()));
    },
  );
}

export function selectUnitByTeamMemberId(teamMemberId: string) {
  return createSelector(selectAllUnits, selectAllUnitPostions, (units, positions) => {
    const position = positions.find((p) => p.teamMemberId === teamMemberId);

    return position == null ? undefined : units.find((u) => u.id === position.unitId);
  });
}

export function byStatusAndNonActiveOperations(
  status: UnitStatus,
  cards: Seq.Indexed<CardSummaryRecord>,
) {
  return (unit: UnitSummaryRecord) => {
    const hasActiveAssignment = cards.valueSeq().some((card) => {
      if (card.unitId !== unit.id) {
        return false;
      }

      if (card.status === CardStatus.Completed || card.status === CardStatus.Closed) {
        return false;
      }

      if (card.operation != null) {
        return (
          card.operation.status !== OperationStatus.Completed &&
          card.operation.status !== OperationStatus.Cancelled
        );
      }

      return card.request == null || card.request.status !== RequestStatus.Cancelled;
    });

    return unit.status === status && !hasActiveAssignment;
  };
}

export function selectNonCandidateUnitsForRequest(requestId: string) {
  return createSelector(
    selectAllUnitSummaryEntities,
    selectTransportOffersByRequestId(requestId),
    (units, offers) => {
      return units.valueSeq().filter((u) => offers.find((o) => o.unitId === u.id) == null);
    },
  );
}

function byHiddenStatus(includehidden: boolean) {
  return (u: UnitSummaryRecord) =>
    includehidden === true ||
    (getOrElse(u.isHidden, false) === false && getOrElse(u.isThirdPartyUnit, false) === false);
}

function matchUnitOrVehicle(
  vehicles: Seq.Indexed<VehicleSummaryRecord>,
  unit: UnitSummaryRecord,
  nameOrTailLike: string | undefined,
): boolean {
  const vehicle = vehicles.find((v) => v.id === unit.vehicleId);
  return (
    nameOrTailLike == null ||
    (nameOrTailLike != null &&
      (unit.abbreviation.toLocaleLowerCase().includes(nameOrTailLike.toLocaleLowerCase()) ||
        (vehicle != null &&
          vehicle.registrationNumber
            .toLocaleLowerCase()
            .includes(nameOrTailLike.toLocaleLowerCase()))))
  );
}
